import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Spin } from 'antd';
import { Header } from './components/Layout/Header';
import { ResizableDashPanels } from './components/Layout/ResizableDashPanels';
import { ProjectSidebar } from './components/Layout/ProjectSidebar';
import { ProjectDetailPanel } from './components/Layout/ProjectDetailPanel';
import { DockingSidebar } from './components/Layout/DockingSidebar';
import { DockingDetailPanel } from './components/Layout/DockingDetailPanel';
import { ProgressSidebar } from './components/Layout/ProgressSidebar';
import { ProgressDetailPanel } from './components/Layout/ProgressDetailPanel';
import type { ProjectContextMode } from './components/Layout/ProjectContextLinks';
import { EchartsMap } from './components/Map/EchartsMap';
import type { MapStatsScope } from './components/Map/types';
import { useMarketProjects } from './hooks/useMarketProjects';
import { useDocking } from './hooks/useDocking';
import { useProgress } from './hooks/useProgress';
import {
  DEFAULT_PROJECTS_DATA_FILTER,
  type QueryMode,
} from './types';
import { DEFAULT_DOCKING_FILTER, DEFAULT_PROGRESS_FILTER } from './types/docking';
import {
  countDockedProjectsByCategory,
  isDockingFilterDirty,
} from './utils/dockingHelpers';
import { isProgressFilterDirty } from './utils/progressHelpers';
import {
  countProjectsByCategory,
  isProjectsFilterDirty,
  projectsForMap,
  projectsInProvince,
} from './utils/marketHelpers';

/**
 * 市场营销数字化管理平台：全屏 3D 地图 + 项目侧栏与详情
 */
function App(): React.ReactElement {
  const market = useMarketProjects();
  const docking = useDocking(market.allProjects);
  const progress = useProgress(market.allProjects);
  const [queryMode, setQueryMode] = useState<QueryMode>('projects');
  /** 三种模式共用的项目选中态；切模式时保留 */
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [mapStatsScope, setMapStatsScope] = useState<MapStatsScope>({
    mode: 'nationalOverview',
    provinceAdcode: null,
  });

  const isDockingMode = queryMode === 'docking';
  const isProgressMode = queryMode === 'progress';

  const setDockingSelectedProjectId = docking.setSelectedProjectId;
  const selectProgressForProject = progress.selectForProject;
  const setProgressSelectedRecordId = progress.setSelectedRecordId;

  const syncSelectionToModes = useCallback(
    (projectId: string | null) => {
      setDockingSelectedProjectId(projectId);
      selectProgressForProject(projectId);
    },
    [setDockingSelectedProjectId, selectProgressForProject],
  );

  const scopedProjectsForSidebar = useMemo(() => {
    if (
      mapStatsScope.mode === 'provinceDrill' &&
      mapStatsScope.provinceAdcode
    ) {
      return projectsInProvince(
        market.projects,
        mapStatsScope.provinceAdcode,
      );
    }
    return market.projects;
  }, [market.projects, mapStatsScope]);

  const scopedDockingProjects = useMemo(() => {
    if (
      mapStatsScope.mode === 'provinceDrill' &&
      mapStatsScope.provinceAdcode
    ) {
      return projectsInProvince(
        docking.mapProjects,
        mapStatsScope.provinceAdcode,
      );
    }
    return docking.mapProjects;
  }, [docking.mapProjects, mapStatsScope]);

  const scopedDockingSummaries = useMemo(() => {
    const ids = new Set(scopedDockingProjects.map((p) => p.id));
    return docking.projectSummaries.filter((s) => ids.has(s.projectId));
  }, [docking.projectSummaries, scopedDockingProjects]);

  const dockingSidebarStatistics = useMemo(
    () => countDockedProjectsByCategory(scopedDockingSummaries),
    [scopedDockingSummaries],
  );

  const scopedProgressProjects = useMemo(() => {
    if (
      mapStatsScope.mode === 'provinceDrill' &&
      mapStatsScope.provinceAdcode
    ) {
      return projectsInProvince(
        progress.mapProjects,
        mapStatsScope.provinceAdcode,
      );
    }
    return progress.mapProjects;
  }, [progress.mapProjects, mapStatsScope]);

  const scopedProgressRecords = useMemo(() => {
    const ids = new Set(scopedProgressProjects.map((p) => p.id));
    return progress.records.filter((r) => ids.has(r.projectId));
  }, [progress.records, scopedProgressProjects]);

  const progressSidebarStatistics = useMemo(
    () => ({
      total: scopedProgressRecords.length,
      completed: scopedProgressRecords.filter(
        (r) => r.completionStatus === 'completed',
      ).length,
      incomplete: scopedProgressRecords.filter(
        (r) => r.completionStatus === 'incomplete',
      ).length,
      overdue: scopedProgressRecords.filter(
        (r) => r.overdueDays != null && r.overdueDays > 0,
      ).length,
    }),
    [scopedProgressRecords],
  );

  const relatedCounts = useMemo(() => {
    if (!selectedProjectId) {
      return { dockingCount: 0, progressCount: 0, incompleteProgressCount: 0 };
    }
    const source =
      docking.allRecords.length > 0 ? docking.allRecords : progress.allRecords;
    const forProject = source.filter((r) => r.projectId === selectedProjectId);
    return {
      dockingCount: forProject.length,
      progressCount: forProject.length,
      incompleteProgressCount: forProject.filter(
        (r) => r.completionStatus === 'incomplete',
      ).length,
    };
  }, [selectedProjectId, docking.allRecords, progress.allRecords]);

  const mapProjects = useMemo(() => {
    const source = isDockingMode
      ? scopedDockingProjects
      : isProgressMode
        ? scopedProgressProjects
        : scopedProjectsForSidebar;
    const inProvinceDrill = mapStatsScope.mode === 'provinceDrill';
    return projectsForMap(source, {
      hideOverseas: inProvinceDrill,
      provinceAdcode: inProvinceDrill
        ? mapStatsScope.provinceAdcode
        : null,
    });
  }, [
    isDockingMode,
    isProgressMode,
    scopedDockingProjects,
    scopedProgressProjects,
    scopedProjectsForSidebar,
    mapStatsScope.mode,
    mapStatsScope.provinceAdcode,
  ]);

  const sidebarStatistics = useMemo(
    () => countProjectsByCategory(scopedProjectsForSidebar),
    [scopedProjectsForSidebar],
  );

  const [mapFocusRequestKey, setMapFocusRequestKey] = useState(0);

  const handleSelectProject = useCallback(
    (projectId: string) => {
      setSelectedProjectId(projectId);
      syncSelectionToModes(projectId);
    },
    [syncSelectionToModes],
  );

  const handleSelectProgressRecord = useCallback(
    (recordId: string) => {
      setProgressSelectedRecordId(recordId);
      const match =
        progress.allRecords.find((r) => r.id === recordId) ??
        progress.records.find((r) => r.id === recordId);
      if (match) {
        setSelectedProjectId(match.projectId);
        setDockingSelectedProjectId(match.projectId);
      }
    },
    [
      setProgressSelectedRecordId,
      progress.allRecords,
      progress.records,
      setDockingSelectedProjectId,
    ],
  );

  const handleReselectSameProject = useCallback(
    (projectId: string) => {
      if (selectedProjectId === projectId) {
        setMapFocusRequestKey((k) => k + 1);
      }
    },
    [selectedProjectId],
  );

  const handleReselectSameProgressRecord = useCallback(
    (recordId: string) => {
      if (progress.selectedRecordId === recordId) {
        setMapFocusRequestKey((k) => k + 1);
      }
    },
    [progress.selectedRecordId],
  );

  const applyQueryMode = useCallback(
    (mode: ProjectContextMode) => {
      setQueryMode(mode);
      if (selectedProjectId) {
        if (mode === 'docking') {
          setDockingSelectedProjectId(selectedProjectId);
        } else if (mode === 'progress') {
          selectProgressForProject(selectedProjectId);
        }
      }
    },
    [
      selectedProjectId,
      setDockingSelectedProjectId,
      selectProgressForProject,
    ],
  );

  const handleQueryModeChange = useCallback(
    (mode: QueryMode) => {
      if (mode === 'projects' || mode === 'docking' || mode === 'progress') {
        applyQueryMode(mode);
      }
    },
    [applyQueryMode],
  );

  const handleNavigateRelated = useCallback(
    (mode: ProjectContextMode) => {
      applyQueryMode(mode);
    },
    [applyQueryMode],
  );

  useEffect(() => {
    if (!selectedProjectId) return;
    const stillExists = market.allProjects.some(
      (p) => p.id === selectedProjectId,
    );
    if (!stillExists) {
      setSelectedProjectId(null);
      syncSelectionToModes(null);
    }
  }, [market.allProjects, selectedProjectId, syncSelectionToModes]);

  useEffect(() => {
    if (!selectedProjectId) return;
    if (
      mapStatsScope.mode !== 'provinceDrill' ||
      !mapStatsScope.provinceAdcode
    ) {
      return;
    }
    // 下钻时仅当当前模式列表里看不到该项目才清空，避免误伤跨模式保留
    const inView =
      (isDockingMode &&
        scopedDockingProjects.some((p) => p.id === selectedProjectId)) ||
      (isProgressMode &&
        scopedProgressProjects.some((p) => p.id === selectedProjectId)) ||
      (!isDockingMode &&
        !isProgressMode &&
        scopedProjectsForSidebar.some((p) => p.id === selectedProjectId));
    if (!inView) {
      setSelectedProjectId(null);
      syncSelectionToModes(null);
    }
  }, [
    selectedProjectId,
    mapStatsScope,
    isDockingMode,
    isProgressMode,
    scopedDockingProjects,
    scopedProgressProjects,
    scopedProjectsForSidebar,
    syncSelectionToModes,
  ]);

  // 进展数据异步到位后，按当前项目补选一条进展
  useEffect(() => {
    if (!selectedProjectId || progress.loading) return;
    if (progress.selectedRecord?.projectId === selectedProjectId) return;
    selectProgressForProject(selectedProjectId);
  }, [
    selectedProjectId,
    progress.loading,
    progress.allRecords,
    progress.selectedRecord?.projectId,
    selectProgressForProject,
  ]);

  // 对接侧选中与全局项目选中保持一致
  useEffect(() => {
    if (docking.selectedProjectId === selectedProjectId) return;
    setDockingSelectedProjectId(selectedProjectId);
  }, [
    selectedProjectId,
    docking.selectedProjectId,
    setDockingSelectedProjectId,
  ]);

  const [mapRightChromeInsetPx, setMapRightChromeInsetPx] = useState(316);

  const restoreDefaultsAvailable = useMemo(() => {
    if (isDockingMode) {
      return (
        selectedProjectId != null || isDockingFilterDirty(docking.filter)
      );
    }
    if (isProgressMode) {
      return (
        selectedProjectId != null ||
        progress.selectedRecordId != null ||
        isProgressFilterDirty(progress.filter)
      );
    }
    return (
      selectedProjectId != null || isProjectsFilterDirty(market.filter)
    );
  }, [
    isDockingMode,
    isProgressMode,
    selectedProjectId,
    docking.filter,
    progress.selectedRecordId,
    progress.filter,
    market.filter,
  ]);

  const dataError =
    market.error ||
    (isDockingMode ? docking.error : null) ||
    (isProgressMode ? progress.error : null);
  const dataLoading =
    market.loading ||
    (isDockingMode && docking.loading) ||
    (isProgressMode && progress.loading);

  const leftPanel = isDockingMode ? (
    <DockingSidebar
      statistics={dockingSidebarStatistics}
      projectSummaries={docking.loading ? [] : scopedDockingSummaries}
      filter={docking.filter}
      setFilter={docking.setFilter}
      selectedProjectId={selectedProjectId}
      onSelectProject={handleSelectProject}
      onReselectSameProject={handleReselectSameProject}
    />
  ) : isProgressMode ? (
    <ProgressSidebar
      statistics={progressSidebarStatistics}
      records={progress.loading ? [] : scopedProgressRecords}
      filter={progress.filter}
      setFilter={progress.setFilter}
      selectedRecordId={progress.selectedRecordId}
      onSelectRecord={handleSelectProgressRecord}
      onReselectSameRecord={handleReselectSameProgressRecord}
    />
  ) : (
    <ProjectSidebar
      projects={market.loading ? [] : scopedProjectsForSidebar}
      statistics={sidebarStatistics}
      filter={market.filter}
      setFilter={market.setFilter}
      filterOptions={market.filterOptions}
      selectedProjectId={selectedProjectId ?? undefined}
      onSelectProject={handleSelectProject}
      onReselectSameProject={handleReselectSameProject}
      onProjectCategoryChange={() => {
        setSelectedProjectId(null);
        syncSelectionToModes(null);
      }}
    />
  );

  const rightPanel = isDockingMode ? (
    <DockingDetailPanel
      selectedProjectId={selectedProjectId}
      projects={market.allProjects}
      records={docking.loading ? [] : docking.selectedProjectRecords}
      progressCount={relatedCounts.progressCount}
      incompleteProgressCount={relatedCounts.incompleteProgressCount}
      onNavigateRelated={handleNavigateRelated}
    />
  ) : isProgressMode ? (
    <ProgressDetailPanel
      record={progress.loading ? null : progress.selectedRecord}
      selectedProjectId={selectedProjectId}
      projects={market.allProjects}
      dockingCount={relatedCounts.dockingCount}
      progressCount={relatedCounts.progressCount}
      incompleteProgressCount={relatedCounts.incompleteProgressCount}
      onNavigateRelated={handleNavigateRelated}
    />
  ) : (
    <ProjectDetailPanel
      selectedProjectId={selectedProjectId ?? undefined}
      projects={market.allProjects}
      dockingCount={relatedCounts.dockingCount}
      progressCount={relatedCounts.progressCount}
      incompleteProgressCount={relatedCounts.incompleteProgressCount}
      onNavigateRelated={handleNavigateRelated}
    />
  );

  return (
    <div className="app-shell app-shell--map">
      <div className="app-map-layer">
        {!dataError && (
          <EchartsMap
            parks={[]}
            selectedParkId={undefined}
            onSelectPark={() => {}}
            marketProjects={!dataLoading ? mapProjects : []}
            selectedProjectId={selectedProjectId ?? undefined}
            onSelectProject={handleSelectProject}
            mapQueryMode="projects"
            onMapStatsScopeChange={setMapStatsScope}
            projectMapFocusRequestKey={mapFocusRequestKey}
            onRestoreDefaults={() => {
              setSelectedProjectId(null);
              syncSelectionToModes(null);
              if (isDockingMode) {
                docking.setFilter(DEFAULT_DOCKING_FILTER);
              } else if (isProgressMode) {
                progress.setFilter(DEFAULT_PROGRESS_FILTER);
              } else {
                market.setFilter(DEFAULT_PROJECTS_DATA_FILTER);
              }
            }}
            restoreDefaultsAvailable={restoreDefaultsAvailable}
            layoutChromeRightInsetPx={mapRightChromeInsetPx}
          />
        )}
        {dataLoading && (
          <div className="app-map-overlay app-map-overlay--loading">
            <Spin size="large" tip="加载项目数据…" />
          </div>
        )}
        {dataError && (
          <div className="app-map-overlay app-map-overlay--error">
            <Alert
              type="error"
              message="数据加载失败"
              description={dataError}
              showIcon
            />
          </div>
        )}
      </div>

      <Header queryMode={queryMode} onQueryModeChange={handleQueryModeChange} />

      {!dataError && (
        <ResizableDashPanels
          leftDefaultWidth={320}
          onChromeRightInsetPx={setMapRightChromeInsetPx}
          left={leftPanel}
          right={rightPanel}
        />
      )}
    </div>
  );
}

export default App;
